<template>
  <div
    class="banner-widget"
    :style="{
      backgroundColor: config.backgroundColor,
      color: config.fontColor,
      width: '100%',
      height: '100%',
    }"
  >
    <div class="banner-content">
      <ul
        class="flip flip-text"
        :class="{ play: isFlipping }"
        :style="{
          height: '100%',
          fontSize: fontSize + 'px',
          boxShadow: `0 2px 5px ${boxShadowColor}`,
        }"
      >
        <li
          v-for="(textObj, index) in config.texts"
          :key="`text-${index}`"
          :class="{
            active: index === currentTextIndex,
            before: index === previousTextIndex,
          }"
        >
          <a href="#">
            <div class="up">
              <div class="shadow"></div>
              <div class="inn">{{ textObj.text }}</div>
            </div>
            <div class="down">
              <div class="shadow"></div>
              <div class="inn">{{ textObj.text }}</div>
            </div>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>


<script lang="ts">
export default {
  name: "BannerWidget",
};
</script>
<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount, watch, defineProps } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  config: any;
}>();

const { t } = useI18n();

const currentTextIndex = ref(0);
const previousTextIndex = ref(0);
const isFlipping = ref(false);
const flipTimer = ref<number | null>(null);

const fontSize = computed(() => {
  // Calculate font size based on banner height and width
  return Math.floor(Math.min(props.config.height, props.config.width) * 0.8);
});

const boxShadowColor = computed(() => {
  return hexToRgba(props.config.backgroundColor, 0.7);
});

function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${
    num & 255
  }, ${alpha})`;
}

function triggerFlipAnimation() {
  const element: any = document.querySelector(".flip-text");
  if (element) {
    element.classList.remove("play");
    void element.offsetWidth; // Force reflow
    element.classList.add("play");
  }
}

function nextText() {
  if (!props.config.texts || props.config.texts.length === 0) return;

  previousTextIndex.value = currentTextIndex.value;
  currentTextIndex.value =
    (currentTextIndex.value + 1) % props.config.texts.length;

  isFlipping.value = true;
  triggerFlipAnimation();

  // Reset flip state after animation
  setTimeout(() => {
    isFlipping.value = false;
  }, 1000);
}

onMounted(() => {
  if (props.config.texts && props.config.texts.length > 1) {
    const duration = parseInt(props.config.transitionDuration) || 3000;
    flipTimer.value = window.setInterval(nextText, duration);
  }
});

onBeforeUnmount(() => {
  if (flipTimer.value) {
    window.clearInterval(flipTimer.value);
  }
});

// Watch for config changes to restart timer
watch(
  () => props.config.transitionDuration,
  (newDuration) => {
    if (flipTimer.value) {
      window.clearInterval(flipTimer.value);
    }
    if (props.config.texts && props.config.texts.length > 1) {
      const duration = parseInt(newDuration) || 3000;
      flipTimer.value = window.setInterval(nextText, duration);
    }
  }
);
</script>

<style scoped>
.banner-widget {
  display: flex !important;
  align-items: center;
  justify-content: center;
  /* border-radius: 8px; */
  overflow: hidden;
  position: relative;
}

.banner-content {
  width: 100%;
  height: 100%;
  /* display: flex;
  align-items: center;
  justify-content: center; */
}

/* Flip Animation Styles - Similar to clock widget */
ul.flip {
  position: relative !important;
  height: 100%;
  font-weight: bold;
  border-radius: 6px;
  list-style: none;
  padding: 0;
  margin: 0;
  min-width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

ul.flip li {
  z-index: 1;
  position: absolute !important;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

ul.flip li.active {
  z-index: 2;
}

ul.flip li a {
  display: block !important;
  height: 100%;
  perspective: 200px;
  text-decoration: none;
}

ul.flip li a div {
  z-index: 1;
  position: absolute !important;
  left: 0;
  width: 100%;
  height: 50%;
  overflow: hidden;
}

ul.flip li a div .shadow {
  position: absolute !important;
  width: 100%;
  height: 100%;
  z-index: 2;
}

ul.flip li a div.up {
  transform-origin: 50% 100%;
  top: 0;
}

ul.flip li a div.down {
  transform-origin: 50% 0%;
  bottom: 0;
}

ul.flip li a div div.inn {
  position: absolute !important;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 200%;
  color: v-bind("config.fontColor");
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  text-align: center;
  background-color: v-bind("config.backgroundColor");
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  white-space: nowrap;
  padding: 0 10px;
  box-sizing: border-box;
}

ul.flip li a div.up div.inn {
  top: 0;
}

ul.flip li a div.down div.inn {
  bottom: 0;
}

/* Animation */
ul.flip.play li.before {
  z-index: 3;
}

ul.flip.play li.active {
  animation: flipActive 0.5s 0.5s linear both;
  z-index: 2;
}

@keyframes flipActive {
  0% {
    z-index: 2;
  }
  5% {
    z-index: 4;
  }
  100% {
    z-index: 4;
  }
}

ul.flip.play li.active .down {
  z-index: 2;
  animation: turnDown 0.5s 0.5s linear both;
}

@keyframes turnDown {
  0% {
    transform: rotateX(90deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}

ul.flip.play li.before .up {
  z-index: 2;
  animation: turnUp 0.5s linear both;
}

@keyframes turnUp {
  0% {
    transform: rotateX(0deg);
  }
  100% {
    transform: rotateX(-90deg);
  }
}

/* Shadow Effects */
ul.flip.play li.before .up .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  animation: showShadow 0.5s linear both;
}

ul.flip.play li.active .up .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  animation: hideShadow 0.5s 0.3s linear both;
}

ul.flip.play li.before .down .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  animation: showShadow 0.5s linear both;
}

ul.flip.play li.active .down .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  animation: hideShadow 0.5s 0.3s linear both;
}

@keyframes showShadow {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes hideShadow {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
